<template>
  <main class="px-4 pt-4">
    <h2
      v-if="company"
      class="mb-2"
    >
      {{ $t('Personal service price') }}. {{ company.name }}
    </h2>
    <div class="d-flex flex-row align-items-baseline">
      <h3 class="mr-auto">
        {{ $t('Services') }}
      </h3>
      <dx-button
        :text="$t('create')"
        type="success"
        styling-mode="contained"
        class="custom-btn mr-2"
        :disabled="createBtnDisabled"
        @click="refreshCompanyPrice"
      />
      <dx-button
        :text="$t('delete')"
        type="success"
        styling-mode="outlined"
        class="custom-btn"
        :disabled="deleteBtnDisabled"
        @click="deleteCompanyPrice"
      />
    </div>
    <dx-data-grid
      ref="servicesGrid"
      class="mb-3"
      :data-source="companyServiceList"
      :remote-operations="remoteOperations"
      :allow-column-reordering="true"
      :show-borders="true"
      :column-min-width="50"
      :column-auto-width="true"
      :allow-column-resizing="true"
      @toolbar-preparing="onServiceToolbarPreparing"
      @content-ready="onServiceContentReady"
    >
      <dx-column
        data-field="railway_route"
        :caption="$t('Route')"
        :allow-editing="false"
      >
        <dx-lookup
          :data-source="routeList"
          value-expr="id"
          display-expr="description"
        />
      </dx-column>
      <dx-column
        data-field="railway_route_service"
        :caption="$t('service')"
        :allow-editing="false"
      >
        <dx-lookup
          :data-source="commonServiceList"
          value-expr="id"
          display-expr="service.description"
        />
      </dx-column>
      <dx-column
        data-field="description"
        :caption="$t('description')"
      />
      <dx-column
        data-field="description_en"
        caption="description"
      />
      <dx-column
        data-field="base_cost"
        :caption="$t('base cost')"
        :allow-editing="false"
      />
      <dx-column
        data-field="cost"
        :caption="$t('Cost')"
      />
      <dx-editing
        :allow-updating="true"
        :use-icons="true"
        mode="row"
      />
      <dx-paging :page-size="20" />
      <dx-pager
        :show-page-size-selector="true"
        :allowed-page-sizes="[10, 15, 20, 50]"
      />
      <dx-export
        :enabled="true"
        file-name="services"
      />
      <dx-group-panel :visible="true" />
    </dx-data-grid>
    <h3>
      {{ $t('Container transfer cost') }}
    </h3>
    <dx-data-grid
      ref="transferGrid"
      :data-source="companyTransferList"
      :remote-operations="remoteOperations"
      :allow-column-reordering="true"
      :show-borders="true"
      :column-min-width="50"
      :column-auto-width="true"
      :allow-column-resizing="true"
      @toolbar-preparing="onTransferToolbarPreparing"
      @content-ready="onTransferContentReady"
    >
      <dx-column
        data-field="railway_route"
        :caption="$t('Route')"
        :allow-editing="false"
      >
        <dx-lookup
          :data-source="routeList"
          value-expr="id"
          display-expr="description"
        />
      </dx-column>
      <dx-column
        data-field="container_type"
        :caption="$t('container type')"
        :allow-editing="false"
      >
        <dx-lookup
          :data-source="containerTypeList"
          value-expr="id"
          display-expr="description"
        />
      </dx-column>
      <dx-column
        data-field="railway_transportation_cost"
        :caption="$t('base cost')"
        :allow-editing="false"
      >
        <dx-lookup
          :data-source="commonTransferList"
          value-expr="id"
          display-expr="cost"
        />
      </dx-column>
      <dx-column
        data-field="cost"
        :caption="$t('Cost')"
      />
      <dx-editing
        :allow-updating="true"
        :use-icons="true"
        mode="row"
      />
      <dx-paging :page-size="20" />
      <dx-pager
        :show-page-size-selector="true"
        :allowed-page-sizes="[10, 15, 20, 50]"
      />
      <dx-export
        :enabled="true"
        file-name="transfer"
      />
      <dx-group-panel :visible="true" />
    </dx-data-grid>
  </main>
</template>

<script>
// prettier-ignore
import {
  DxDataGrid,
  DxColumn,
  DxEditing,
  DxPaging,
  DxPager,
  DxGroupPanel,
  DxExport,
  DxLookup,
} from 'devextreme-vue/data-grid';

import { DxButton } from 'devextreme-vue';

import { mapState } from 'vuex';
import ruMessages from 'devextreme/localization/messages/ru.json';
import { locale as localize, loadMessages } from 'devextreme/localization';

import createCustomStore from '@/utils/create-custom-store';

export default {
  name: 'CompanyServices',
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxPaging,
    DxPager,
    DxGroupPanel,
    DxExport,
    DxLookup,
    DxButton,
  },
  props: {
    companyId: [String, Number],
  },
  data: vm => ({
    company: null,
    remoteOperations: {
      sorting: true,
      filtering: true,
      paging: true,
    },
    companyServiceList: createCustomStore(`/planner/api/company/${vm.companyId}/service/`),
    commonServiceList: createCustomStore('/planner/api/railway_route_all_services/'),
    routeList: createCustomStore('/planner/api/railway_route/'),
    companyTransferList: createCustomStore(`/planner/api/company/${vm.companyId}/transportation_cost/`),
    commonTransferList: createCustomStore('/planner/api/railway_route_all_transportation_cost/'),
    containerTypeList: createCustomStore('planner/api/container_type/'),
    isServiceGridEmpty: true,
    isTransferGridEmpty: true,
  }),
  computed: {
    ...mapState(['user']),
    createBtnDisabled() {
      return !this.isServiceGridEmpty || !this.isTransferGridEmpty;
    },
    deleteBtnDisabled() {
      return this.isServiceGridEmpty && this.isTransferGridEmpty;
    },
  },
  created() {
    loadMessages(ruMessages);
    localize(this.$i18n.locale);
    this.getCompany();
  },
  methods: {
    onServiceToolbarPreparing(e) {
      e.toolbarOptions.items.unshift({
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'ace-icon icon-refresh',
          onClick: this.refreshServiceDataGrid,
        },
      });
    },
    onTransferToolbarPreparing(e) {
      e.toolbarOptions.items.unshift({
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'ace-icon icon-refresh',
          onClick: this.refreshTransferDataGrid,
        },
      });
    },
    refreshServiceDataGrid() {
      this.$refs.servicesGrid.instance.refresh();
    },
    refreshTransferDataGrid() {
      this.$refs.transferGrid.instance.refresh();
    },
    async refreshCompanyPrice() {
      await this.$http.post(`/planner/api/company/${this.companyId}/company_price_refresh/`);
      this.refreshServiceDataGrid();
      this.refreshTransferDataGrid();
    },
    async deleteCompanyPrice() {
      await this.$http.post(`/planner/api/company/${this.companyId}/company_price_delete/`);
      this.refreshServiceDataGrid();
      this.refreshTransferDataGrid();
    },
    async getCompany() {
      this.company = await this.$http.get(`/planner/api/company/${this.companyId}/`);
    },
    onServiceContentReady(e) {
      this.isServiceGridEmpty = !e.component.getVisibleRows().length;
    },
    onTransferContentReady(e) {
      this.isTransferGridEmpty = !e.component.getVisibleRows().length;
    },
  },
};
</script>
